import { db, type GradeQueueItem } from './db';
import { studyApi } from '../../api/endpoints/study';
import { toLocalIsoString } from '../datetime';

export interface EnqueuePayload {
  cardId: number;
  variant: string;
  rating: 1 | 2 | 3 | 4;
  durationMs: number;
  studySessionId: number;
  idempotencyKey: string;
  ratedAt: number;
  /** Offset użytkownika (minuty na wschód od UTC) w momencie nauki. Persisted do queue. */
  tzOffsetMinutes: number;
}

export interface FlushResult {
  /** Liczba success-flushed items (200 OK). */
  flushed: number;
  /** Card IDs które były 404 — UI pokazuje 1× toast "{N} ocen pominięto". */
  deletedCardIds: number[];
  /** True jeśli flush napotkał 401 — sync-controller triggeruje silent refresh. */
  authRefreshNeeded: boolean;
  /** True jeśli flush przerwany przez retry cap (3× 5xx) — strip pokazuje "sync-error". */
  syncError: boolean;
}

interface ApiErrorLike {
  status?: number;
}

function getStatus(err: unknown): number | undefined {
  if (typeof err === 'object' && err !== null && 'status' in err) {
    return (err as ApiErrorLike).status;
  }
  if (typeof err === 'object' && err !== null && 'problem' in err) {
    const problem = (err as { problem: { status?: number } }).problem;
    return problem?.status;
  }
  return undefined;
}

export async function enqueueGrade(payload: EnqueuePayload): Promise<number> {
  const item: Omit<GradeQueueItem, 'id'> = {
    payload,
    status: 'pending',
    retryCount: 0,
    createdAt: payload.ratedAt,
  };
  return db.gradeQueue.add(item);
}

export async function getPendingCount(): Promise<number> {
  return db.gradeQueue.where('status').equals('pending').count();
}

export async function getTotalQueueCount(): Promise<number> {
  return db.gradeQueue.count();
}

/**
 * Undo offline (§3.8): usuwa NAJNOWSZY (po `createdAt`) wpis kolejki dla (cardId, variant) i
 * zwraca `true`, gdy taki istniał. Transakcja `rw` (jak claim w `flushQueue`) chroni przed
 * race z flush'em — jeśli flush już sklaimował i wysłał wpis (DELETE po 200), `popLastGrade`
 * go nie znajdzie → zwróci `false` → consumer fallbackuje do endpointu (online) lub blocked.
 */
export async function popLastGrade(cardId: number, variant: string): Promise<boolean> {
  return db.transaction('rw', db.gradeQueue, async () => {
    const candidates = await db.gradeQueue
      .filter((it) => it.payload.cardId === cardId && it.payload.variant === variant)
      .sortBy('createdAt');
    const last = candidates[candidates.length - 1];
    if (!last) return false;
    await db.gradeQueue.delete(last.id!);
    return true;
  });
}

/**
 * Read-only: czy istnieje wpis kolejki do cofnięcia dla (cardId, variant). Używane przez
 * reaktywny stan disabled/tooltip przycisku „Cofnij" w sesji nauki (bez mutacji kolejki).
 */
export async function peekLastGrade(cardId: number, variant: string): Promise<boolean> {
  const candidates = await db.gradeQueue
    .filter((it) => it.payload.cardId === cardId && it.payload.variant === variant)
    .sortBy('createdAt');
  return candidates.length > 0;
}

const BACKOFF_MS = [1000, 2000, 4000];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface FlushOptions {
  /** Override sleep dla testów (np. () => Promise.resolve()). Default = real setTimeout. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Sequencyjny flush queue. Per-error-class handling per spec §5.3.
 *
 * Multi-tab lock: w jednej transakcji `rw` claim'ujemy wszystkie pending → flushing.
 * Drugi tab w tym samym czasie wejdzie w pustą listę → no-op.
 *
 * Retry semantics: 5xx bumpuje `retryCount` w DB; w ramach JEDNEGO flush'a
 * powtarzamy do 3× z exp backoff (1s/2s/4s); jeśli wszystkie 3 fail → STOP całego flush
 * z `syncError: true` (heartbeat za 5min spróbuje znowu).
 */
export async function flushQueue(options?: FlushOptions): Promise<FlushResult> {
  const sleepFn = options?.sleep ?? sleep;
  const result: FlushResult = {
    flushed: 0,
    deletedCardIds: [],
    authRefreshNeeded: false,
    syncError: false,
  };

  const claimed = await db.transaction('rw', db.gradeQueue, async () => {
    const items = await db.gradeQueue.where('status').equals('pending').sortBy('createdAt');
    for (const it of items) {
      await db.gradeQueue.update(it.id!, { status: 'flushing' });
    }
    return items;
  });

  for (const item of claimed) {
    let attempt = item.retryCount;
    let sent = false;
    while (!sent && attempt < BACKOFF_MS.length) {
      try {
        await studyApi.grade({
          cardId: item.payload.cardId,
          variant: item.payload.variant,
          rating: item.payload.rating,
          durationMs: item.payload.durationMs,
          // Reconstruct ISO z lokalnym offsetem zapisanym w czasie nauki — replay z innego
          // dnia/strefy/DST nadal trafia do właściwego lokalnego dnia (nie dnia replay).
          reviewedAt: toLocalIsoString(
            new Date(item.payload.ratedAt),
            item.payload.tzOffsetMinutes,
          ),
          idempotencyKey: item.payload.idempotencyKey,
          studySessionId: item.payload.studySessionId,
        });
        await db.gradeQueue.delete(item.id!);
        result.flushed += 1;
        sent = true;
      } catch (err) {
        const status = getStatus(err);
        if (status === 404) {
          await db.gradeQueue.delete(item.id!);
          result.deletedCardIds.push(item.payload.cardId);
          sent = true;
        } else if (status === 409) {
          await db.gradeQueue.delete(item.id!);
          sent = true;
        } else if (status === 401) {
          await db.gradeQueue.update(item.id!, { status: 'pending' });
          result.authRefreshNeeded = true;
          return result;
        } else {
          attempt += 1;
          await db.gradeQueue.update(item.id!, { retryCount: attempt });
          if (attempt >= BACKOFF_MS.length) {
            // Cap reached this flush — revert to pending AND reset retryCount=0 so next
            // heartbeat/online-event flush gets a fresh budget. Without this reset the
            // item permanently strands: next flush claim phase sets status=flushing, then
            // `attempt = retryCount = 3 >= BACKOFF_MS.length` makes the while loop exit
            // immediately without delete/revert/post → dead-locked invisibly in IndexedDB.
            await db.gradeQueue.update(item.id!, { status: 'pending', retryCount: 0 });
            result.syncError = true;
            return result;
          }
          await sleepFn(BACKOFF_MS[attempt - 1]!);
        }
      }
    }
  }

  return result;
}
